import React, { useId, useRef } from "react";
import { TASK_LABEL, TASKS_LABEL } from "./taskCopy";
import { TASK_WORKSPACE_SECTIONS } from "./taskWorkspaceProjection";
import { canonicalizeSection, currentActionSection, sectionAvailable } from "./taskSections";
import "./TaskWorkspace.css";

const TONE_MARK = {
  complete: "✓",
  current: "•",
  needs_attention: "•",
  blocked: "•",
  waiting: "…",
};

export default function TaskWorkspace({
  title,
  repoLabel,
  projection,
  section,
  onSectionChange,
  showHeader = true,
  children,
}) {
  const navId = useId();
  const mainRef = useRef(null);
  const requested = canonicalizeSection(section);
  const current =
    requested && TASK_WORKSPACE_SECTIONS.some((item) => item.id === requested)
      ? requested
      : currentActionSection(projection);

  const changeSection = (next) => {
    const id = canonicalizeSection(next) || current;
    if (typeof onSectionChange === "function") onSectionChange(id);
    window.requestAnimationFrame(() => {
      mainRef.current?.focus?.();
    });
  };

  const onTabKeyDown = (event) => {
    const ids = TASK_WORKSPACE_SECTIONS.map((item) => item.id);
    const index = Math.max(0, ids.indexOf(current));
    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      event.preventDefault();
      changeSection(ids[(index + 1) % ids.length]);
    } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      event.preventDefault();
      changeSection(ids[(index - 1 + ids.length) % ids.length]);
    } else if (event.key === "Home") {
      event.preventDefault();
      changeSection(ids[0]);
    } else if (event.key === "End") {
      event.preventDefault();
      changeSection(ids[ids.length - 1]);
    }
  };

  return (
    <div className="task-workspace" data-testid="task-workspace" data-section={current}>
      <a className="task-workspace-skip" href="#task-workspace-main">
        Skip to {TASK_LABEL} content
      </a>
      {showHeader ? (
        <header className="task-workspace-header">
          <nav className="task-workspace-breadcrumb" aria-label="Breadcrumb">
            <ol>
              <li>{repoLabel || "Repository"}</li>
              <li>{TASKS_LABEL}</li>
              <li>{TASK_LABEL}</li>
            </ol>
          </nav>
          <h2 className="task-workspace-title" data-testid="task-workspace-title">
            {title || `Untitled ${TASK_LABEL}`}
          </h2>
          <p className="task-workspace-repo">{repoLabel}</p>
          <div className="task-workspace-status-row">
            <span
              className="task-workspace-status"
              data-testid="task-workspace-status"
              data-status={projection?.status || ""}
            >
              {projection?.statusLabel || "Working"}
            </span>
            <p className="task-workspace-owner" data-testid="task-workspace-owner">
              Waiting on: {projection?.owner?.label || "someone"}
            </p>
          </div>
          {projection?.primaryAction ? (
            <button
              type="button"
              className="task-workspace-primary"
              data-testid="task-workspace-primary-action"
              onClick={() => changeSection(projection.primaryAction.section)}
            >
              {projection.primaryAction.label}
            </button>
          ) : null}
        </header>
      ) : (
        <div className="task-workspace-status-row">
          <span
            className="task-workspace-status"
            data-testid="task-workspace-status"
            data-status={projection?.status || ""}
          >
            {projection?.statusLabel || "Working"}
          </span>
          <p className="task-workspace-owner" data-testid="task-workspace-owner">
            Waiting on: {projection?.owner?.label || "someone"}
          </p>
        </div>
      )}

      <div
        className="task-workspace-nav"
        role="tablist"
        aria-label={`${TASK_LABEL} sections`}
        data-testid="task-workspace-nav"
        onKeyDown={onTabKeyDown}
      >
        {TASK_WORKSPACE_SECTIONS.map((item) => {
          const state = projection?.sectionStates?.[item.id];
          const selected = current === item.id;
          const mark = TONE_MARK[state?.tone] || "";
          const available = sectionAvailable(item.id, projection);
          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              id={`${navId}-${item.id}`}
              aria-selected={selected}
              aria-controls="task-workspace-main"
              tabIndex={selected ? 0 : -1}
              data-testid={`task-workspace-nav-${item.id}`}
              data-tone={state?.tone || "available"}
              aria-disabled={available ? undefined : true}
              onClick={() => changeSection(item.id)}
            >
              <span>{item.label}</span>
              {mark ? (
                <span className="task-workspace-nav-mark" aria-hidden="true">
                  {mark}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
      <label className="visually-hidden" htmlFor={`${navId}-select`}>
        {TASK_LABEL} section
      </label>
      <select
        id={`${navId}-select`}
        className="task-workspace-nav-select"
        data-testid="task-workspace-nav-select"
        value={current}
        onChange={(event) => changeSection(event.target.value)}
      >
        {TASK_WORKSPACE_SECTIONS.map((item) => (
          <option key={item.id} value={item.id}>
            {item.label}
          </option>
        ))}
      </select>

      <div
        id="task-workspace-main"
        className="task-workspace-main"
        data-testid="task-workspace-main"
        tabIndex={-1}
        ref={mainRef}
        role="tabpanel"
        aria-labelledby={`${navId}-${current}`}
      >
        {children}
      </div>
    </div>
  );
}
